import type { FsFile, FsFolder, FsNode } from '../types/files';

export function isFolder(node: FsNode): node is FsFolder {
  return node.kind === 'folder';
}

export function isFile(node: FsNode): node is FsFile {
  return node.kind === 'file';
}

export function childrenOf(nodes: FsNode[], parentId: string | null): FsNode[] {
  return nodes.filter((node) => !node.trashed && node.parentId === parentId);
}

export function findNode(nodes: FsNode[], id: string | null): FsNode | undefined {
  if (!id) return undefined;
  return nodes.find((node) => node.id === id);
}

/** Root-to-node folder chain, excluding the node itself. */
export function pathTo(nodes: FsNode[], id: string | null): FsFolder[] {
  const chain: FsFolder[] = [];
  let cursor = findNode(nodes, id);
  while (cursor && cursor.parentId) {
    const parent = findNode(nodes, cursor.parentId);
    if (!parent || !isFolder(parent)) break;
    chain.unshift(parent);
    cursor = parent;
  }
  return chain;
}

export function descendantIds(nodes: FsNode[], folderId: string): string[] {
  const result: string[] = [];
  const walk = (parentId: string) => {
    nodes
      .filter((node) => node.parentId === parentId)
      .forEach((node) => {
        result.push(node.id);
        if (isFolder(node)) walk(node.id);
      });
  };
  walk(folderId);
  return result;
}

export interface FolderStats {
  fileCount: number;
  sizeKb: number;
}

export function folderStats(nodes: FsNode[], folderId: string): FolderStats {
  const ids = new Set(descendantIds(nodes, folderId));
  const files = nodes.filter(
    (node) => ids.has(node.id) && isFile(node) && !node.trashed,
  ) as FsFile[];
  return {
    fileCount: files.length,
    sizeKb: files.reduce((total, file) => total + file.sizeKb, 0),
  };
}

/** Expands a selection of folders + files into the concrete files inside it. */
export function expandToFiles(nodes: FsNode[], ids: string[]): FsFile[] {
  const collected = new Map<string, FsFile>();
  ids.forEach((id) => {
    const node = findNode(nodes, id);
    if (!node) return;
    if (isFile(node)) {
      collected.set(node.id, node);
      return;
    }
    descendantIds(nodes, node.id).forEach((childId) => {
      const child = findNode(nodes, childId);
      if (child && isFile(child) && !child.trashed) {
        collected.set(child.id, child);
      }
    });
  });
  return [...collected.values()];
}

export function isWithin(
  nodes: FsNode[],
  candidateId: string,
  ancestorId: string,
): boolean {
  return descendantIds(nodes, ancestorId).includes(candidateId);
}

export function totalUsedKb(nodes: FsNode[]): number {
  return nodes
    .filter((node): node is FsFile => isFile(node) && !node.trashed)
    .reduce((total, file) => total + file.sizeKb, 0);
}
